import { z } from "zod";
import { WEIGHT_KG_MAX, WEIGHT_KG_MIN } from "../units.js";

// Read-side shape returned by GET /api/stats/exercise/:slug. Buckets are
// 10 kg wide on estimated 1RM (Epley), keyed by the floor of the bucket
// (e.g. "90" for [90, 100) kg). Age buckets reuse the profile vocabulary.
//
// k-anonymity: only buckets with at least 10 distinct contributors are
// included. The threshold is enforced at the API layer; the wire format
// has no signal that smaller buckets exist.
export const StatsBucketCount = z.object({
  // Bounded to the WEIGHT range (0..2000), not E1RM_KG_MAX (5000): whether an
  // estimate above 2000 kg should be accepted, clamped, or rejected by this
  // endpoint is a product question about stats display, not a units question
  // — it is named here, not resolved.
  e1rmBucketKg: z.number().int().min(WEIGHT_KG_MIN).max(WEIGHT_KG_MAX),
  count: z.number().int().min(10),
});
export type StatsBucketCount = z.infer<typeof StatsBucketCount>;

export const ExerciseStats = z.object({
  exerciseId: z.string().min(1),
  // Map age-bucket → ordered list of e1RM-bucket counts. Empty object
  // means no cohort cleared the privacy threshold yet.
  byAgeBucket: z.record(z.string(), z.array(StatsBucketCount)),
  // Total contributors across all visible buckets. Useful to display
  // "based on N people in your age group" without revealing per-bucket
  // splits below the k-anon threshold.
  visibleContributors: z.number().int().min(0),
});
export type ExerciseStats = z.infer<typeof ExerciseStats>;

export const E1RM_BUCKET_KG = 10;

// Floor to the nearest E1RM_BUCKET_KG kg. Used at write and read time;
// changing this constant invalidates the bucket layout, so it's central.
export const e1rmBucket = (kg: number) =>
  Math.max(0, Math.floor(kg / E1RM_BUCKET_KG) * E1RM_BUCKET_KG);
